import React, { useState, useEffect, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolButton from '../toolbars/ToolButton';
import { ApplicationView } from '@aha-app/mvc';
import ColorSwatch, { ColorPalette, OpacitySlider } from './ColorSwatch';
import { colorPaletteForObject } from '../utils/DefaultsManager';
import { type Instance as TippyInstance } from 'tippy.js/index';

const Popup: React.FC<{
  currentColor: string;
  setCurrentColor: (color: string) => void;
  selection: fabric.Object;
  hidePopup: () => void;
  showOpacitySilder?: boolean;
  setOpacity: (opacity: number) => void;
}> = ApplicationView(
  ({
    currentColor,
    setCurrentColor,
    selection,
    hidePopup,
    showOpacitySilder = true,
    setOpacity,
  }) => {
    const handleOpacityChange = (opacity: number) => {
      selection.setOnObjects('opacity', opacity);
      setOpacity(opacity);
    };

    const ticks = '[2, 5, 10,20,30,40,50,60,70,80,90,100]';
    const opacity = selection.firstObject().get('opacity') || 1;

    return (
      <ColorPalette
        palette={colorPaletteForObject(selection.firstObject())}
        currentColor={currentColor}
        setCurrentColor={(color: string) => {
          setCurrentColor(color);
          selection.setOnObjects('fill', color);
        }}
        hidePopup={hidePopup}
      >
        {showOpacitySilder && (
          <OpacitySlider
            ticks={ticks}
            opacity={opacity}
            handleOpacityChange={handleOpacityChange}
          />
        )}
      </ColorPalette>
    );
  }
);

const fillColorButton = ({
  showOpacitySilder = false,
}): ToolButtonComponent => {
  return ApplicationView(({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    const obj = selection.firstObject();
    const fillColor = obj.get('fill');
    const initialOpacity = obj.get('opacity') || 1;
    const [currentColor, setCurrentColor] = useState(fillColor);
    const [opacity, setOpacity] = useState(initialOpacity);

    // Update color if the props change.
    useEffect(() => {
      setCurrentColor(fillColor);
    }, [fillColor]);

    return (
      <ToolButton
        popup={
          <Popup
            currentColor={currentColor}
            setCurrentColor={setCurrentColor}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
            showOpacitySilder={showOpacitySilder}
            setOpacity={setOpacity}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        <ColorSwatch color={currentColor} opacity={opacity} swatchIsInToolbar />
      </ToolButton>
    );
  });
};

class ToolFillColor extends Tool {
  static toolName = 'Fill color';

  static buttonComponent() {
    return fillColorButton({ showOpacitySilder: true });
  }
}

class ToolFillColorWithoutOpacity extends Tool {
  static toolName = 'Fill color';

  static buttonComponent() {
    return fillColorButton({ showOpacitySilder: false });
  }
}

export { ToolFillColor, ToolFillColorWithoutOpacity };
